// Das Bankkonto: was reinkam, was rausging, und wozu es gehört.
//
// ══ Was hier NOCH NICHT passiert ══
//
// Es ist keine Bank angebunden. Das Modul steht bereit, die Tabellen stehen seit
// Migration 019 da — es fließt nur noch nichts hinein. Genau das sagt die Seite
// auch, statt eine leere Liste zu zeigen, die aussieht wie "keine Umsätze".
// Der Unterschied zwischen "nichts da" und "nichts angeschlossen" ist der
// Unterschied zwischen Ruhe und einem Anruf.
//
// ══ Der Abgleich ══
//
// Eine Überweisung einer offenen Rechnung zuzuordnen HEISST, sie zu bezahlen.
// Wir buchen sie deshalb auch so — über denselben Weg wie die Kasse, nicht über
// einen zweiten eigenen. Zwei Wege, die dasselbe Geld buchen, sind einer zu
// viel; das hat dieses Projekt schon einmal Doppelrechnungen gekostet.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type BewegungsStatus = "unmatched" | "matched" | "ignored";

export interface Kontobewegung {
  id: string;
  booking_date: string;
  value_date: string | null;
  /** Positiv = Eingang, negativ = Ausgang. */
  amount_cents: number;
  currency: string;
  counterparty: string | null;
  counterparty_iban: string | null;
  purpose: string | null;
  status: BewegungsStatus;
  matched_invoice_id: string | null;
  matched_at: string | null;
  hat_rechnung_bezahlt: boolean;
  rechnung?: {
    id: string;
    invoice_number: string;
    total_cents: number;
    status: string;
  } | null;
}

export interface Bankkonto {
  id: string;
  name: string;
  iban: string | null;
  is_active: boolean;
}

export type BewegungsFilter = "alle" | "eingang" | "ausgang" | "offen";

/**
 * Ist überhaupt ein Konto hinterlegt?
 *
 * Solange nicht, ist eine leere Umsatzliste kein Befund, sondern nur die
 * Abwesenheit einer Leitung. Die Seite muss das auseinanderhalten können.
 */
export function useBankVerbunden() {
  return useQuery({
    queryKey: ["bankkonten"],
    queryFn: async (): Promise<Bankkonto[]> => {
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, name, iban, is_active")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useKontobewegungen(filter: BewegungsFilter = "alle") {
  return useQuery({
    queryKey: ["kontobewegungen", filter],
    queryFn: async (): Promise<Kontobewegung[]> => {
      let q = supabase
        .from("bank_transactions")
        // Der Join MUSS benannt sein. bank_transactions hat zwei Wege zu
        // profiles (matched_by) und einen zu invoices — sobald ein zweiter
        // dazukommt, scheitert ein unbenannter Embed mit PGRST201 und die Seite
        // bleibt STILL leer. Dreimal in diesem Projekt passiert.
        .select(
          "*, rechnung:invoices!bank_transactions_matched_invoice_id_fkey(id, invoice_number, total_cents, status)",
        )
        .order("booking_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(200);

      if (filter === "eingang") q = q.gt("amount_cents", 0);
      if (filter === "ausgang") q = q.lt("amount_cents", 0);
      if (filter === "offen") q = q.eq("status", "unmatched");

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Kontobewegung[];
    },
  });
}

/** Summen über das, was gerade in der Liste steht. */
export function summen(bewegungen: Kontobewegung[]) {
  let ein = 0;
  let aus = 0;
  let offen = 0;
  for (const b of bewegungen) {
    if (b.amount_cents > 0) ein += b.amount_cents;
    else aus += Math.abs(b.amount_cents);
    if (b.status === "unmatched") offen++;
  }
  return { ein, aus, saldo: ein - aus, offen };
}

/** Offene Rechnungen — die Auswahl beim Zuordnen. */
export function useOffeneRechnungen(suche = "") {
  return useQuery({
    queryKey: ["offene-rechnungen-zuordnung", suche],
    queryFn: async () => {
      let q = supabase
        .from("invoices")
        .select(
          "id, invoice_number, total_cents, status, due_at, customer:customers(display_name)",
        )
        .in("status", ["open", "overdue"])
        .order("due_at", { ascending: true })
        .limit(50);

      if (suche.trim()) q = q.ilike("invoice_number", `%${suche.trim()}%`);

      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Bewegung ↔ Rechnung verknüpfen.
 *
 * Passt der Betrag exakt und ist die Rechnung noch offen, wird sie damit auch
 * bezahlt gebucht — das IST ja die Bedeutung des Vorgangs.
 *
 * Passt er NICHT (Teilzahlung, Rundung, Sammelüberweisung), wird nur verknüpft
 * und sonst nichts. Eine Zahlung über 4,00 € darf keine Rechnung über 200 €
 * abhaken, bloß weil jemand sie in dieselbe Zeile gezogen hat. Was dann zu tun
 * ist, entscheidet ein Mensch.
 */
export function useZuordnen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      bewegung: Kontobewegung;
      rechnung: { id: string; total_cents: number; status: string };
    }) => {
      const { bewegung, rechnung } = v;

      const passtGenau =
        bewegung.amount_cents === rechnung.total_cents &&
        (rechnung.status === "open" || rechnung.status === "overdue");

      if (passtGenau) {
        // Derselbe Weg wie die Kasse (useAlsBezahltBuchen) — kein zweiter.
        const { error } = await supabase
          .from("invoices")
          .update({
            status: "paid",
            paid_cents: rechnung.total_cents,
            paid_at: new Date().toISOString(),
          })
          .eq("id", rechnung.id);
        if (error) throw error;
      }

      const { error } = await supabase
        .from("bank_transactions")
        .update({
          matched_invoice_id: rechnung.id,
          status: "matched",
          matched_at: new Date().toISOString(),
          matched_by: (await supabase.auth.getUser()).data.user?.id ?? null,
          hat_rechnung_bezahlt: passtGenau,
        })
        .eq("id", bewegung.id);
      if (error) throw error;

      return { bezahltGebucht: passtGenau };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kontobewegungen"] });
      qc.invalidateQueries({ queryKey: ["offene-rechnungen-zuordnung"] });
      qc.invalidateQueries({ queryKey: ["rechnungen"] });
      qc.invalidateQueries({ queryKey: ["kassenlage"] });
      qc.invalidateQueries({ queryKey: ["rechnungen-kunde"] });
      qc.invalidateQueries({ queryKey: ["zahlungsstand"] });
    },
  });
}

/**
 * Zuordnung zurücknehmen.
 *
 * Die Rechnung wird NUR dann wieder auf offen gesetzt, wenn genau diese
 * Zuordnung sie bezahlt gebucht hat (`hat_rechnung_bezahlt`). Sonst würde eine
 * versehentlich zugeordnete Überweisung eine längst bar bezahlte Rechnung
 * wieder aufreißen — und der Kunde bekäme eine Mahnung für etwas, das er
 * bezahlt hat.
 */
export function useZuordnungLoesen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bewegung: Kontobewegung) => {
      if (bewegung.hat_rechnung_bezahlt && bewegung.matched_invoice_id) {
        const { error } = await supabase
          .from("invoices")
          .update({ status: "open", paid_cents: 0, paid_at: null })
          .eq("id", bewegung.matched_invoice_id);
        if (error) throw error;
      }

      const { error } = await supabase
        .from("bank_transactions")
        .update({
          matched_invoice_id: null,
          status: "unmatched",
          matched_at: null,
          matched_by: null,
          hat_rechnung_bezahlt: false,
        })
        .eq("id", bewegung.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kontobewegungen"] });
      qc.invalidateQueries({ queryKey: ["offene-rechnungen-zuordnung"] });
      qc.invalidateQueries({ queryKey: ["rechnungen"] });
      qc.invalidateQueries({ queryKey: ["kassenlage"] });
      qc.invalidateQueries({ queryKey: ["rechnungen-kunde"] });
      qc.invalidateQueries({ queryKey: ["zahlungsstand"] });
    },
  });
}
